import React, { useEffect, useState } from 'react';
import styles from '../../../styles/LoginRegister.module.css';
import buttonStyles from '../../../styles/Buttons.module.css';
import { useLogin } from '../../../hooks/useLogin';
import PasswordToggleButton from '../../common/PasswordToggleButton';

interface UserLoginProps {
    email?: string;
    onLogin: () => void | Promise<void>;
}

const UserLogin: React.FC<UserLoginProps> = ({ email, onLogin }) => {
    const [loginEmail, setLoginEmail] = useState(email || '');
    const [loginPassword, setLoginPassword] = useState('');
    const [showForgot, setShowForgot] = useState(false);
    const [resetMessage, setResetMessage] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);
    
    const { login, forgotPassword, isLoading, error } = useLogin();
    const [showUnmasked, setShowUnmasked] = useState<boolean>(false);
    

    useEffect(() => {
        if (email) setLoginEmail(email);
    }, [email]);

    useEffect(() => {
        setLocalError(null);
        setResetMessage('');
    }, [showForgot]);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setLocalError(null);
        setResetMessage('');
        
        const result = await login({ email: loginEmail, password: loginPassword });
        
        if (result.success) {
            try {
                await onLogin();
            } catch (err: any) {
                console.error('Error during post-login processing:', err);
                const errorMsg = err?.message || 'Login succeeded but failed to load user profile.';
                setLocalError(errorMsg);
            }
        } else {
            const errorMsg = result.error || 'Login failed. Please try again.';
            setLocalError(errorMsg);
        }
    };

    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setResetMessage('');
        setLocalError(null);
        
        const result = await forgotPassword(loginEmail);
        
        if (result.success) {
            setResetMessage(result.message || 'If that email exists, a reset link has been sent.');
        } else {
            const errorMsg = result.error || 'Failed to send reset email.';
            setLocalError(errorMsg);
        }
    };

    return (
        <div className={styles.formSection}>
            <h2 className={styles.formTitle}>User Login</h2>
            {(localError || error) && <p className={styles.errorMessage}>{localError || error}</p>}
            {resetMessage && <p className={styles.successMessage}>{resetMessage}</p>}

            {!showForgot ? (
                <form onSubmit={handleLogin} className={styles.form}>
                    <div className={styles.fieldContainer}>
                        <label className={styles.fieldLabel}>Email</label>
                        <input
                            type="email"
                            value={loginEmail}
                            onChange={e => setLoginEmail(e.target.value)}
                            required
                            disabled={!!email} // Disable if email is pre-filled
                            className={styles.fieldInput}
                            style={email ? { backgroundColor: '#f8fafc', color: '#64748b' } : {}}
                        />
                    </div>

                    <div className={styles.fieldContainer}>
                        <label className={styles.fieldLabel}>Password</label>
                        <div className={styles.passwordFieldWrapper}>
                            <input
                                type={showUnmasked ? "text" : "password"}
                                value={loginPassword}
                                onChange={e => setLoginPassword(e.target.value)}
                                required
                                className={styles.fieldInput}
                            />
                            <PasswordToggleButton
                                visible={showUnmasked}
                                onClick={() => setShowUnmasked(!showUnmasked)}
                            />
                        </div>
                    </div>

                    <button type="submit" disabled={isLoading} className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}>
                        {isLoading ? 'Logging in...' : 'Login'}
                    </button>

                    <button
                        type="button"
                        className={`${buttonStyles.button} ${buttonStyles.link}`}
                        onClick={() => setShowForgot(true)}
                    >
                        Forgot password?
                    </button>
                </form>
            ) : (
                <form onSubmit={handleForgotPassword} className={styles.form}>
                    <div className={styles.fieldContainer}>
                        <label className={styles.fieldLabel}>Enter your email</label>
                        <input
                            type="email"
                            value={loginEmail}
                            onChange={e => setLoginEmail(e.target.value)}
                            required
                            className={styles.fieldInput}
                        />
                    </div>

                    <button type="submit" disabled={isLoading} className={`${buttonStyles.button} ${buttonStyles.formPrimary}`}>
                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>

                    <button
                        type="button"
                        className={`${buttonStyles.button} ${buttonStyles.link}`}
                        onClick={() => setShowForgot(false)}
                    >
                        Back to Login
                    </button>
                </form>
            )}
        </div>
    );
};

export default UserLogin;
